import React, { ClipboardEvent, forwardRef, useMemo } from 'react';
import { InputProps, InputRef } from 'antd';
import { InputMask, format } from '@react-input/mask';

import { StyledPhoneField } from './PhoneInputStyles';
import { applyFullPhonePaste, scheduleAutofilledPhone, toNationalPhoneDigits, trackPhonePaste } from './phonePaste';

export const defaultPhoneMask = '+380 (__)-___-__-__';

const PHONE_REPLACEMENT = { _: /\d/ };

const phoneMaskOptions = {
  mask: defaultPhoneMask,
  replacement: PHONE_REPLACEMENT,
};

const bindAutofillCapture = (input: HTMLInputElement | null) => {
  if (!input || input.dataset.phoneAutofillBound === '1') {
    return;
  }

  input.dataset.phoneAutofillBound = '1';
  // Capture phase runs before @react-input/mask and can still read the
  // autofilled value before the mask reverts it.
  input.addEventListener(
    'input',
    () => {
      scheduleAutofilledPhone(input);
    },
    true,
  );
};

const PhoneField = forwardRef<HTMLInputElement, InputProps>((props, ref) => (
  <StyledPhoneField
    {...props}
    ref={(instance: InputRef | null) => {
      const input = instance?.input ?? null;
      bindAutofillCapture(input);
      if (typeof ref === 'function') {
        ref(input);
      } else if (ref) {
        ref.current = input;
      }
    }}
  />
));

PhoneField.displayName = 'PhoneField';

type PhoneInputProps = Omit<InputProps, 'value' | 'defaultValue'> & {
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
};

const toMaskedPhone = (value?: string) => {
  if (value == null || value === '') {
    return defaultPhoneMask;
  }

  // Already masked (literals/placeholders). Re-running format() collapses
  // `separate` holes and moves the caret on mid-number edits.
  if (/[()\s_-]/.test(value)) {
    return value;
  }

  return format(toNationalPhoneDigits(value), phoneMaskOptions);
};

const PhoneInput = ({ value, defaultValue, onPaste, ...props }: PhoneInputProps) => {
  const sourceValue =
    value != null && value !== '' ? value : defaultValue != null && defaultValue !== '' ? defaultValue : '';

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    onPaste?.(event);
    if (event.defaultPrevented) {
      return;
    }

    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const text = event.clipboardData?.getData('text') ?? '';
    if (input && applyFullPhonePaste(input, text)) {
      event.preventDefault();
    }
  };

  const maskedValue = useMemo(() => toMaskedPhone(sourceValue), [sourceValue]);

  return (
    <InputMask
      component={PhoneField}
      mask={defaultPhoneMask}
      replacement={PHONE_REPLACEMENT}
      showMask
      separate
      {...props}
      type="tel"
      autoComplete="tel"
      inputMode="tel"
      onPaste={handlePaste}
      value={maskedValue}
      track={trackPhonePaste}
    />
  );
};

export default PhoneInput;
